import type {
  AnalyticsSchemaDraftCategory,
  AnalyticsSchemaDraftDocument,
  AnalyticsSchemaDraftEnum,
  AnalyticsSchemaDraftEnumValue,
  AnalyticsSchemaDraftEvent,
  AnalyticsSchemaDraftProperty,
} from '@voogzy/contracts';

export function cloneSchemaDocument(document: AnalyticsSchemaDraftDocument): AnalyticsSchemaDraftDocument {
  return structuredClone(document);
}

export function createCategory(document: AnalyticsSchemaDraftDocument): AnalyticsSchemaDraftCategory {
  const key = uniqueKey('new_category', readCategoryKeys(document));
  return {
    id: crypto.randomUUID(),
    wireCode: nextCategoryCode(document),
    key,
    displayName: 'New category',
    description: '',
  };
}

export function createEnum(document: AnalyticsSchemaDraftDocument): AnalyticsSchemaDraftEnum {
  const key = uniqueKey('new_enum', readEnumKeys(document));
  return {
    id: crypto.randomUUID(),
    key,
    csharpTypeName: toPascalCase(key),
    displayName: 'New enum',
    description: '',
    values: [createEnumValue([], 0)],
  };
}

export function createEnumValue(existing: AnalyticsSchemaDraftEnumValue[], suggestedValue?: number): AnalyticsSchemaDraftEnumValue {
  const keys = new Set<string>();
  let maximum = -1;
  for (const value of existing) {
    keys.add(value.key);
    if (value.value !== null && value.value > maximum) {
      maximum = value.value;
    }
  }
  return {
    id: crypto.randomUUID(),
    value: suggestedValue ?? maximum + 1,
    key: uniqueKey('new_value', keys),
    displayName: 'New value',
    description: '',
  };
}

export function createEvent(document: AnalyticsSchemaDraftDocument): AnalyticsSchemaDraftEvent {
  const key = uniqueKey('new_event', readEventKeys(document));
  return {
    id: crypto.randomUUID(),
    categoryId: document.categories[0]?.id ?? null,
    eventCode: nextEventCode(document),
    eventKey: key,
    displayName: 'New event',
    description: '',
    properties: [],
  };
}

export function duplicateEvent(
  document: AnalyticsSchemaDraftDocument,
  source: AnalyticsSchemaDraftEvent,
): AnalyticsSchemaDraftEvent {
  const copy = structuredClone(source);
  copy.id = crypto.randomUUID();
  copy.eventCode = nextEventCode(document);
  copy.eventKey = uniqueKey(`${source.eventKey}_copy`, readEventKeys(document));
  copy.displayName = `${source.displayName} copy`;
  for (const property of copy.properties) {
    property.id = crypto.randomUUID();
  }
  return copy;
}

export function createProperty(existing: AnalyticsSchemaDraftProperty[]): AnalyticsSchemaDraftProperty {
  const keys = new Set<string>();
  for (const property of existing) {
    keys.add(property.key);
  }
  const key = uniqueKey('new_property', keys);
  return {
    id: crypto.randomUUID(),
    key,
    displayName: 'New property',
    description: '',
    type: 'string',
    optional: false,
    unit: null,
    enumId: null,
    commonDimension: null,
  };
}

export function parseNullableInteger(value: string): number | null {
  if (value.trim() === '') {
    return null;
  }
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) ? parsed : null;
}

export function slugify(value: string): string {
  const normalized = value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  if (normalized === '') {
    return '';
  }
  return /^[a-z]/.test(normalized) ? normalized : `value_${normalized}`;
}

export function toPascalCase(value: string): string {
  const parts = value.split('_');
  let result = '';
  for (const part of parts) {
    result += part.charAt(0).toUpperCase() + part.slice(1);
  }
  return result;
}

function nextCategoryCode(document: AnalyticsSchemaDraftDocument): number {
  let maximum = 0;
  for (const category of document.categories) {
    if (category.wireCode !== null && category.wireCode > maximum) {
      maximum = category.wireCode;
    }
  }
  return maximum + 1;
}

function nextEventCode(document: AnalyticsSchemaDraftDocument): number {
  let maximum = 0;
  for (const event of document.events) {
    if (event.eventCode !== null && event.eventCode > maximum) {
      maximum = event.eventCode;
    }
  }
  return maximum + 1;
}

function readCategoryKeys(document: AnalyticsSchemaDraftDocument): Set<string> {
  const keys = new Set<string>();
  for (const category of document.categories) {
    keys.add(category.key);
  }
  return keys;
}

function readEnumKeys(document: AnalyticsSchemaDraftDocument): Set<string> {
  const keys = new Set<string>();
  for (const definition of document.enums) {
    keys.add(definition.key);
  }
  return keys;
}

function readEventKeys(document: AnalyticsSchemaDraftDocument): Set<string> {
  const keys = new Set<string>();
  for (const event of document.events) {
    keys.add(event.eventKey);
  }
  return keys;
}

function uniqueKey(base: string, existing: ReadonlySet<string>): string {
  if (!existing.has(base)) {
    return base;
  }
  let suffix = 2;
  while (existing.has(`${base}_${suffix}`)) {
    suffix += 1;
  }
  return `${base}_${suffix}`;
}
